import { useCallback, useState, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { logout } from "../../redux/actions/auth";
import styles from "./Topbar.module.css";

const Topbar = ({ show, handleToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const logOut = useCallback(() => {
    dispatch(logout());
    navigate("/login", { replace: true });
  }, [dispatch, navigate]);

  // --- Search Bar State and Logic ---
  const pages = [
    { name: "Home", path: "/" },
    { name: "Login", path: "/login" },
    { name: "Register", path: "/register" },
    { name: "Resources (Grid)", path: "/resources" },
    { name: "Resource Create", path: "/resources/new" },
    { name: "Resource Image Process", path: "/resources/processImage" },
    { name: "Resource Social Post", path: "/resources/socialPost" },
    { name: "Resource Collection Process", path: "/resources/processCollection" },
    { name: "Resource File Process", path: "/resources/processFile" },
    { name: "Resource Tag Process", path: "/resources/processTag" },
    { name: "Resource Convert File", path: "/resources/convert-file" },
    { name: "Resource Bulk Edit", path: "/resources/bulkEdit" },
    { name: "Resource Search", path: "/resources/search" },
    { name: "Collections", path: "/collections" },
    { name: "Files", path: "/files" },
    { name: "Events", path: "/events" },
    { name: "Event Create", path: "/events/new" },
    { name: "Contacts", path: "/contacts" },
    { name: "Orders", path: "/orders" },
    
    // Analytics & Reports Section
    { name: "Reports Dashboard", path: "/reports" },
    { name: "Dashboard Overview", path: "/reports/overview" },
    { name: "SEO Health Report", path: "/reports/seo" },
    { name: "File Summary Report", path: "/reports/file-summary" },
    { name: "File Analytics", path: "/reports/file" },
    { name: "Search Analytics", path: "/reports/search" },
    { name: "Tag Management", path: "/reports/tags" },
    { name: "SEO URLs Management", path: "/reports/meta" },
    { name: "Replace Tags Tool", path: "/reports/replace-tags" },
    { name: "Download Analytics", path: "/reports/download" },
    
    { name: "File Converter", path: "/convert" },
    { name: "Invoices", path: "/invoices" },
    { name: "Payments", path: "/payments" },
    { name: "Users", path: "/users" },
    { name: "User Create", path: "/users/new" },
    { name: "Profile", path: "/profile" },
    { name: "Profile Settings", path: "/profile/settings" },
    { name: "Scan AI", path: "/scan-ai" },
  ];
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const inputRef = useRef();

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (value.trim() === "") {
      setSuggestions([]);
      setShowSuggestions(false);
      setHighlighted(-1);
      return;
    }
    const filtered = pages.filter((p) =>
      p.name.toLowerCase().includes(value.toLowerCase())
    );
    setSuggestions(filtered);
    setShowSuggestions(true);
    setHighlighted(-1);
  };

  const handleSuggestionClick = (page) => {
    setSearch("");
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlighted(-1);
    navigate(page.path);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      setHighlighted((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      setHighlighted((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === "Enter") {
      if (highlighted >= 0 && suggestions[highlighted]) {
        handleSuggestionClick(suggestions[highlighted]);
      } else if (suggestions.length === 1) {
        handleSuggestionClick(suggestions[0]);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 100);
  };


  return (
    <header className={`${styles.topbar} ${show ? styles.show : ""}`} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 24px', minHeight: 56 }}>
      <button className={styles.toggleBtn} onClick={() => handleToggle()} style={{ marginRight: 16, marginLeft: 0, alignSelf: 'center' }}>
        <i className="bi bi-list"></i>
      </button>
      {/* Search Bar - moved left, vertically aligned */}
      <div style={{ width: 320, maxWidth: 400, position: "relative", alignSelf: 'center' }}>
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          placeholder="Search pages..."
          value={search}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          onFocus={() => search && setShowSuggestions(true)}
          onBlur={handleBlur}
          style={{ minWidth: 200, borderRadius: 6, height: 40, paddingLeft: 12 }}
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#fff",
            zIndex: 1000,
            border: "1px solid #eee",
            borderTop: "none",
            margin: 0,
            padding: 0,
            listStyle: "none",
            maxHeight: 200,
            overflowY: "auto"
          }}>
            {suggestions.map((page, idx) => (
              <li
                key={page.path}
                onMouseDown={() => handleSuggestionClick(page)}
                style={{
                  padding: "8px 12px",
                  background: idx === highlighted ? "#f5f5f5" : "#fff",
                  cursor: "pointer"
                }}
              >
                {page.name}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div style={{ flex: 1 }} />
      
      {/* Notification Bell - REMOVED */}
      
      <Dropdown className={`${styles.userMenu}`}>
        <Dropdown.Toggle variant="default" id="dropdown-basic">
          {/* <i className="bi bi-person-fill me-2"></i> */}
          <i className="bi bi-person-check me-2"></i>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item href={"/profile"} className="py-1 text-muted">
            <i className="bi bi-person-vcard me-3"></i>
            <span className="small">{"Profile"}</span>
          </Dropdown.Item>
          <Dropdown.Item href="/profile/settings" className="py-1 text-muted">
            <i className="bi bi-gear-wide-connected me-3"></i>
            <span className="small">{"Settings"}</span>
          </Dropdown.Item>
          <Dropdown.Item
            href={""}
            className="py-1 text-muted"
            onClick={(e) => {
              e.preventDefault();
              logOut();
            }}
          >
            <i className="bi bi-door-open-fill me-3"></i>
            <span className="small">{"Log out"}</span>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </header>
  );
};

export default Topbar;
